import React from 'react';
import classNames from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import styles from './ReactionAvatarStack.module.scss';
import ReactionAvatar from './ReactionAvatar';
import useAppSelector from '../../app/redux/useAppSelector';
import Tooltip from '../../components/Tooltip';
import Button from '../../components/Button';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

const REACTION_FRAGMENT = gql(`
  fragment ReactionAvatarStack_reaction on PublicReactionFields {
    id
    insertedAt
    ...ReactionAvatar_reaction
  }
`);

type ReactionFragment = DocumentType<typeof REACTION_FRAGMENT>;

interface ReactionAvatarStackProps {
  reactions: ReactionFragment[];
  ring?: boolean;
  withinOverlay?: boolean;
  maxItemsShown?: number;
  additionalNode?: React.ReactNode;
  onEmojiClick?: (reaction: ReactionFragment) => void;
  wrap?: boolean;
}

const ReactionAvatarStack = ({
  reactions,
  ring,
  withinOverlay,
  additionalNode,
  maxItemsShown,
  onEmojiClick,
  wrap,
}: ReactionAvatarStackProps) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const sortedReactions = _.sortBy(reactions, (reaction) =>
    moment(reaction.insertedAt).valueOf(),
  );

  const slicedReactions =
    maxItemsShown != null
      ? sortedReactions.slice(0, maxItemsShown)
      : sortedReactions;

  const currentUserEmojis = reactions
    .filter((reaction) => reaction.author.id === currentUser.id)
    .map((reaction) => reaction.emoji);
  const overflowCount = reactions.length - (slicedReactions.length - 1);

  return (
    <div
      className={classNames(styles.container, {
        [styles.hasAdditionalNode]: additionalNode != null,
        [styles.wrap]: wrap,
      })}
    >
      {slicedReactions.map((reaction, i) => {
        if (overflowCount > 1 && i === slicedReactions.length - 1) {
          return (
            <div className={styles.avatarContainer} key="overflow-count">
              <div className={styles.counter}>+{overflowCount}</div>
            </div>
          );
        }

        const isFromCurrentUser = reaction.author.id === currentUser.id;
        const isEmojiAlreadyTaken =
          !isFromCurrentUser && currentUserEmojis.includes(reaction.emoji);

        const avatar = (
          <ReactionAvatar
            ring={ring}
            reaction={reaction}
            active={isFromCurrentUser}
            withinOverlay={withinOverlay}
          />
        );

        if (onEmojiClick && !isEmojiAlreadyTaken) {
          return (
            <div className={styles.avatarContainer} key={reaction.id}>
              <Tooltip
                trigger="hover"
                placement="top"
                popOverPortal={document.body}
                tooltip={
                  isFromCurrentUser
                    ? 'Remove my reaction'
                    : `Also add ${reaction.emoji} reaction`
                }
              >
                <Button onClick={() => onEmojiClick(reaction)}>{avatar}</Button>
              </Tooltip>
            </div>
          );
        }

        return (
          <div className={styles.avatarContainer} key={reaction.id}>
            {avatar}
          </div>
        );
      })}
      {additionalNode}
    </div>
  );
};

export default ReactionAvatarStack;
